import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { 
    ChefHat, 
    ArrowRight, 
    Sparkles, 
    Crown,
    Check,
    ShoppingCart,
    Calendar,
    Heart,
    Utensils,
    TrendingDown,
    Brain,
    ChevronRight,
    Tag,
    Lightbulb,
    ChevronDown,
    DollarSign,
    Clock,
    Users,
    Target,
    Zap
} from 'lucide-react';

export const HomePage = () => {
    const { user, isPremium } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({ recipes_count: 0, tips_count: 0, users_count: 0 });
    const [featuredRecipes, setFeaturedRecipes] = useState([]);
    const [featuredDeals, setFeaturedDeals] = useState([]);
    const [tips, setTips] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [statsRes, recipesRes, dealsRes, tipsRes] = await Promise.all([
                api.getStats(),
                api.getFeaturedRecipes(),
                api.getDeals(),
                api.getTips()
            ]);
            setStats(statsRes.data);
            setFeaturedRecipes(recipesRes.data.slice(0, 2));
            setFeaturedDeals(dealsRes.data.slice(0, 2));
            setTips(tipsRes.data.slice(0, 2));
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const premiumFeatures = [
        "Menu de 5 repas par semaine, prêt à utiliser",
        "Liste d'épicerie intelligente, déjà construite",
        "Modifie les ingrédients selon tes goûts",
        "Meilleurs rabais intégrés automatiquement"
    ];

    // Section card component for consistency
    const SectionCard = ({ title, icon: Icon, iconBg, children, linkTo, linkText }) => (
        <div className="bg-white rounded-2xl border-2 border-foreground shadow-[4px_4px_0px_0px_#1e293b] overflow-hidden">
            <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-bold text-lg text-foreground">{title}</h3>
                    </div>
                </div>
            </div>
            <div className="p-4 space-y-3">
                {children}
            </div>
            <div className="px-4 pb-4">
                <Button 
                    variant="ghost" 
                    onClick={() => navigate(linkTo)}
                    className="w-full justify-center text-primary hover:bg-primary/10 font-medium"
                >
                    {linkText}
                    <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen overflow-hidden bg-gradient-to-b from-orange-50 via-white to-green-50">
            
            {/* Hero Section - New Value Proposition */}
            <section className="relative py-8 md:py-12 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 left-0 w-48 h-48 bg-orange-200 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-green-200 rounded-full blur-3xl opacity-30 translate-x-1/3 translate-y-1/3" />
                <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-purple-200 rounded-full blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2" />
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    
                    {/* Main Headline - NEW */}
                    <div className="text-center mb-8">
                        <span className="text-4xl md:text-5xl mb-4 block">🦎</span>
                        <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground leading-tight mb-4">
                            Tu choisis le besoin.
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-purple-600 to-orange-500">
                                On te trouve la solution.
                            </span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            Selon ton budget, ton temps et ce que t'as déjà sous la main.
                        </p>
                    </div>

                    {/* Quick Needs Selector - Interactive */}
                    <div className="bg-white rounded-2xl border-2 border-foreground shadow-[6px_6px_0px_0px_#1e293b] p-5 md:p-8 mb-8">
                        
                        <p className="text-center font-bold text-foreground mb-5">
                            🎯 Qu'est-ce que tu cherches aujourd'hui?
                        </p>

                        {/* Quick Need Buttons */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            <button 
                                onClick={() => navigate('/recipes?budget=tight')}
                                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-all group"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                    <DollarSign className="w-6 h-6 text-white" />
                                </div>
                                <span className="font-bold text-foreground text-sm">Fin de mois</span>
                                <span className="text-xs text-muted-foreground">≤2.50$/portion</span>
                            </button>
                            
                            <button 
                                onClick={() => navigate('/recipes?max_time=30')}
                                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-all group"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                    <Clock className="w-6 h-6 text-white" />
                                </div>
                                <span className="font-bold text-foreground text-sm">Vite fait</span>
                                <span className="text-xs text-muted-foreground">≤30 min</span>
                            </button>
                            
                            <button 
                                onClick={() => navigate('/recipes?household=solo')}
                                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 transition-all group"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                    <Users className="w-6 h-6 text-white" />
                                </div>
                                <span className="font-bold text-foreground text-sm">Solo</span>
                                <span className="text-xs text-muted-foreground">1 personne</span>
                            </button>
                            
                            <button 
                                onClick={() => navigate('/recipes?household=family')}
                                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-400 transition-all group"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                    <Heart className="w-6 h-6 text-white" />
                                </div>
                                <span className="font-bold text-foreground text-sm">Famille</span>
                                <span className="text-xs text-muted-foreground">4+ personnes</span>
                            </button>
                        </div>

                        {/* Divider */}
                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex-1 h-px bg-gray-200"></div>
                            <span className="text-sm text-muted-foreground font-medium">ou</span>
                            <div className="flex-1 h-px bg-gray-200"></div>
                        </div>

                        {/* Browse All + Premium CTA */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={() => navigate('/recipes')}
                                className="py-5 px-6 text-base font-bold rounded-xl border-2 border-foreground hover:bg-gray-50"
                            >
                                <ChefHat className="w-5 h-5 mr-2" />
                                Voir toutes les recettes
                            </Button>
                            
                            {isPremium ? (
                                <Button
                                    size="lg"
                                    onClick={() => navigate('/weekly-pack')}
                                    className="py-5 px-6 text-base font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl border-2 border-foreground shadow-[3px_3px_0px_0px_#1e293b]"
                                >
                                    <Calendar className="w-5 h-5 mr-2" />
                                    Mon Pack de la Semaine
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    size="lg"
                                    onClick={() => navigate('/register')}
                                    className="py-5 px-6 text-base font-bold bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl border-2 border-foreground shadow-[3px_3px_0px_0px_#1e293b]"
                                >
                                    <Crown className="w-5 h-5 mr-2" />
                                    Essayer Premium gratuit
                                    <Sparkles className="w-5 h-5 ml-2" />
                                </Button>
                            )}
                        </div>
                        
                        {!user && (
                            <p className="text-center text-sm text-muted-foreground mt-4">
                                Déjà membre ? {' '}
                                <Link to="/login" className="text-primary font-bold hover:underline">
                                    Se connecter
                                </Link>
                            </p>
                        )}
                    </div>

                    {/* Premium Features - Compact */}
                    <div className="bg-gradient-to-r from-orange-50 via-purple-50 to-green-50 rounded-2xl border-2 border-foreground p-5 md:p-6">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <Crown className="w-5 h-5 text-orange-500" />
                            <span className="font-bold text-foreground">Avec Premium, tout est déjà fait pour toi</span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {premiumFeatures.map((feature, index) => (
                                <div key={index} className="flex items-start gap-2 text-sm">
                                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                    <span className="text-foreground">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Sections - 2 items each with "En voir plus" */}
            <section className="py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        
                        {/* Rabais Section */}
                        <SectionCard 
                            title="Rabais" 
                            icon={Tag} 
                            iconBg="bg-red-500"
                            linkTo="/deals"
                            linkText="En voir plus..."
                        >
                            {loading ? (
                                <div className="space-y-3">
                                    <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                                    <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                                </div>
                            ) : featuredDeals.length > 0 ? (
                                featuredDeals.map((deal) => (
                                    <div 
                                        key={deal.id} 
                                        className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-200 hover:bg-red-100 transition-colors cursor-pointer"
                                        onClick={() => navigate('/deals')}
                                    >
                                        <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                            -{deal.discount_percent}%
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-foreground truncate">{deal.product_name}</p>
                                            <p className="text-xs text-muted-foreground">{deal.store_name}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-green-600 text-sm">{deal.sale_price?.toFixed(2)}$</p>
                                            <p className="text-xs text-gray-400 line-through">{deal.original_price?.toFixed(2)}$</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-muted-foreground text-sm py-4">Aucun rabais disponible</p>
                            )}
                        </SectionCard>

                        {/* Recettes Section */}
                        <SectionCard 
                            title="Recettes" 
                            icon={Utensils} 
                            iconBg="bg-orange-500"
                            linkTo="/recipes"
                            linkText="En voir plus..."
                        >
                            {loading ? (
                                <div className="space-y-3">
                                    <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                                    <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                                </div>
                            ) : featuredRecipes.length > 0 ? (
                                featuredRecipes.map((recipe) => (
                                    <div 
                                        key={recipe.id} 
                                        className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-200 hover:bg-orange-100 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/recipes/${recipe.id}`)}
                                    >
                                        <div className="flex-shrink-0 w-12 h-12 bg-orange-200 rounded-lg overflow-hidden">
                                            {recipe.image_url ? (
                                                <img src={recipe.image_url} alt={recipe.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <ChefHat className="w-6 h-6 text-orange-500" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-foreground truncate">{recipe.title}</p>
                                            <p className="text-xs text-muted-foreground">{recipe.prep_time + recipe.cook_time} min • {recipe.cost_per_serving?.toFixed(2)}$/portion</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-muted-foreground text-sm py-4">Aucune recette disponible</p>
                            )}
                        </SectionCard>

                        {/* Astuces Section */}
                        <SectionCard 
                            title="Astuces" 
                            icon={Lightbulb} 
                            iconBg="bg-yellow-500"
                            linkTo="/tips"
                            linkText="En voir plus..."
                        >
                            {loading ? (
                                <div className="space-y-3">
                                    <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                                    <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                                </div>
                            ) : tips.length > 0 ? (
                                tips.map((tip) => (
                                    <div 
                                        key={tip.id} 
                                        className="flex items-start gap-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200 hover:bg-yellow-100 transition-colors cursor-pointer"
                                        onClick={() => navigate('/tips')}
                                    >
                                        <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mt-0.5">
                                            <Lightbulb className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-foreground line-clamp-1">{tip.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-2">{tip.content}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-muted-foreground text-sm py-4">Aucune astuce disponible</p>
                            )}
                        </SectionCard>
                    </div>

                    {/* Secondary Navigation */}
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            variant="outline"
                            size="lg"
                            onClick={() => navigate('/recipes')}
                            className="rounded-xl border-2 border-foreground bg-white hover:bg-gray-50 shadow-[2px_2px_0px_0px_#1e293b] hover:shadow-[3px_3px_0px_0px_#1e293b] transition-all"
                        >
                            <Utensils className="w-4 h-4 mr-2" />
                            Toutes les recettes
                        </Button>
                        
                        <Button
                            variant="outline"
                            size="lg"
                            onClick={() => navigate('/guide')}
                            className="rounded-xl border-2 border-foreground bg-white hover:bg-gray-50 shadow-[2px_2px_0px_0px_#1e293b] hover:shadow-[3px_3px_0px_0px_#1e293b] transition-all"
                        >
                            <Heart className="w-4 h-4 mr-2" />
                            Comment ça marche
                        </Button>
                    </div>
                </div>
            </section>

            {/* Simple Footer Stats */}
            <section className="py-8 bg-foreground text-white">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16">
                        <div className="text-center">
                            <p className="font-heading text-3xl font-black text-orange-400">{stats.recipes_count}+</p>
                            <p className="text-white/70 text-sm">Recettes</p>
                        </div>
                        <div className="text-center">
                            <p className="font-heading text-3xl font-black text-green-400">{stats.tips_count}+</p>
                            <p className="text-white/70 text-sm">Astuces</p>
                        </div>
                        <div className="text-center">
                            <p className="font-heading text-3xl font-black text-purple-400">{stats.users_count}+</p>
                            <p className="text-white/70 text-sm">Membres</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
